import { CoralogixLogSeverity, CoralogixRum } from '@coralogix/browser';
import { webStorageUtil } from 'state/webStorage.state.service';
import { ELocalStorageKeys } from '../constants/enums';
import { getEnvVariable } from '@appcharge/shared-ui';

export interface HealthMetrics {
  type: 'health_check';
  healthType: 'startup' | 'periodic' | 'shutdown' | 'api_call' | 'user_action' | 'error';
  sessionDuration?: number;
  isLoggedIn?: boolean;
  playerId?: string;
  playerCountry?: string;
  environment?: string;
  userAgent?: string;
  timestamp: string;
  memoryUsage?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  } | null;
  performance?: {
    navigationStart: number;
    loadEventEnd: number;
    domContentLoadedEventEnd: number;
  };
  apiMetrics?: {
    endpoint: string;
    method: string;
    responseTime: number;
    statusCode?: number;
  };
  userAction?: {
    action: string;
    component: string;
    duration?: number;
  };
  errorDetails?: {
    message: string;
    stack?: string;
    type: string;
  };
  additionalData?: Record<string, any>;
}

export class HealthMonitor {
  private static instance: HealthMonitor;
  private sessionStartTime: number = Date.now();
  private healthCheckInterval: NodeJS.Timeout | null = null;

  private constructor() { }

  static getInstance(): HealthMonitor {
    if (!HealthMonitor.instance) {
      HealthMonitor.instance = new HealthMonitor();
    }
    return HealthMonitor.instance;
  }

  private getBaseHealthData(): Partial<HealthMetrics> {
    const playerData = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);
    const sessionToken = webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN);
    const sessionDuration = Date.now() - this.sessionStartTime;

    return {
      sessionDuration: Math.round(sessionDuration / 1000), // in seconds
      isLoggedIn: !!sessionToken,
      playerId: playerData?.playerId || 'anonymous',
      playerCountry: playerData?.playerCountry || 'unknown',
      environment: getEnvVariable('REACT_APP_ENV', process.env.REACT_APP_ENV) || 'unknown',
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      memoryUsage: (performance as any).memory ? {
        usedJSHeapSize: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024), // MB
        totalJSHeapSize: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024), // MB
        jsHeapSizeLimit: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024), // MB
      } : null,
      performance: {
        navigationStart: (performance as any).timing?.navigationStart || 0,
        loadEventEnd: (performance as any).timing?.loadEventEnd || 0,
        domContentLoadedEventEnd: (performance as any).timing?.domContentLoadedEventEnd || 0,
      },
    };
  }

  logHealth(healthType: HealthMetrics['healthType'], additionalData?: Record<string, any>): void {
    try {
      if (typeof CoralogixRum === 'undefined' || !CoralogixRum.log) {
        console.warn('Coralogix RUM not available for health log');
        return;
      }

      const baseData = this.getBaseHealthData();
      const healthData: HealthMetrics = {
        type: 'health_check',
        healthType,
        timestamp: baseData.timestamp || new Date().toISOString(),
        sessionDuration: baseData.sessionDuration,
        isLoggedIn: baseData.isLoggedIn,
        playerId: baseData.playerId,
        playerCountry: baseData.playerCountry,
        environment: baseData.environment,
        userAgent: baseData.userAgent,
        memoryUsage: baseData.memoryUsage,
        performance: baseData.performance,
        ...additionalData,
      };

      CoralogixRum.log(CoralogixLogSeverity.Info, `Health check: ${healthType}`, healthData);

      if (process.env.NODE_ENV === 'development') {
        console.log(`Health log sent: ${healthType}`, healthData);
      }
    } catch (error) {
      console.error('Failed to send health log:', error);
    }
  }

  logApiCall(endpoint: string, method: string, responseTime: number, statusCode?: number): void {
    this.logHealth('api_call', {
      apiMetrics: {
        endpoint,
        method,
        responseTime,
        statusCode,
      },
    });
  }

  logUserAction(action: string, component: string, duration?: number): void {
    this.logHealth('user_action', {
      userAction: {
        action,
        component,
        duration,
      },
    });
  }

  logError(error: Error, context?: string): void {
    this.logHealth('error', {
      errorDetails: {
        message: error.message,
        stack: error.stack,
        type: error.name,
      },
      additionalData: {
        context,
      },
    });
  }

  startPeriodicHealthChecks(intervalMinutes: number = 5): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.logHealth('startup');

    this.healthCheckInterval = setInterval(() => {
      this.logHealth('periodic');
    }, intervalMinutes * 60 * 1000);
  }

  stopPeriodicHealthChecks(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    this.logHealth('shutdown');
  }

  getSessionDuration(): number {
    return Math.round((Date.now() - this.sessionStartTime) / 1000);
  }
}

// Export singleton instance
export const healthMonitor = HealthMonitor.getInstance(); 